(function () {
  class LuxurySteeringSelector {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.config = this.readConfig();
      this.product = this.readProduct();
      this.colorOptionIndex = this.findColorOptionIndex();
      this.state = {
        variantId: this.config.defaultVariantId,
        voucherCode: '',
        voucherApplied: false,
        voucherLoading: false,
      };
      this.cacheDom();
      this.bindEvents();
      this.setVariant(this.state.variantId, { silent: true });
      this.updateAddToCartLabel();
      this.restoreVoucherState();
      this.initRebuy();
    }

    readConfig() {
      const el = this.root.querySelector('[data-lss-config]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        console.warn('[LSS] Invalid config JSON', err);
        return {};
      }
    }

    readProduct() {
      const el = this.root.querySelector('[data-lss-product]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        console.warn('[LSS] Invalid product JSON', err);
        return null;
      }
    }

    findColorOptionIndex() {
      if (!this.product?.options?.length) return 0;
      const idx = this.product.options.findIndex((name) => /color|colour/i.test(name));
      return idx >= 0 ? idx : 0;
    }

    cacheDom() {
      this.mainImage = this.root.querySelector('[data-lss-main-image]');
      this.selectedColorLabel = this.root.querySelector('[data-lss-selected-color]');
      this.voucherInput = this.root.querySelector('[data-lss-voucher-input]');
      this.voucherRow = this.root.querySelector('[data-lss-voucher-row]');
      this.voucherApplyBtn = this.root.querySelector('[data-lss-voucher-apply]');
      this.voucherActionText = this.root.querySelector('[data-lss-voucher-action-text]');
      this.voucherError = this.root.querySelector('[data-lss-voucher-error]');
      this.priceWrap = this.root.querySelector('[data-lss-price-wrap]');
      this.addToCartBtn = this.root.querySelector('[data-lss-add-to-cart]');
      this.addToCartText = this.root.querySelector('[data-lss-add-to-cart-text]');
      this.addToCartIcon = this.root.querySelector('.lss-add-to-cart__icon');
      this.swatchButtons = Array.from(this.root.querySelectorAll('[data-lss-swatch]'));
      this.rebuyUi = this.root.querySelector('[data-lss-rebuy-ui]');
      this.rebuyHost = this.root.querySelector('[data-lss-rebuy-host]');
    }

    bindEvents() {
      this.swatchButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const variantId = Number(button.dataset.variantId);
          if (variantId) this.setVariant(variantId);
        });
      });

      if (this.voucherApplyBtn) {
        this.voucherApplyBtn.addEventListener('click', () => {
          if (this.state.voucherApplied) {
            this.removeVoucher();
          } else {
            this.applyVoucher();
          }
        });
      }

      if (this.voucherInput) {
        this.voucherInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && !this.state.voucherApplied) {
            event.preventDefault();
            this.applyVoucher();
          }
        });
      }

      const voucherField = this.root.querySelector('.lss-voucher-field');
      if (voucherField && this.voucherInput) {
        voucherField.addEventListener('click', () => {
          if (this.state.voucherApplied) return;
          this.voucherInput.focus();
        });
      }

      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
      }
    }

    getVariantById(variantId) {
      return (this.product?.variants || []).find((variant) => Number(variant.id) === Number(variantId)) || null;
    }

    getVariantColor(variant) {
      if (!variant) return '';
      const key = `option${this.colorOptionIndex + 1}`;
      return variant[key] || variant.title || '';
    }

    getVariantImage(variant) {
      if (!variant) return this.config.fallbackImage || '';
      if (variant.featured_image?.src) return variant.featured_image.src;
      if (typeof variant.featured_image === 'string') return variant.featured_image;
      if (variant.image_id && this.product?.images?.length) {
        const image = this.product.images.find((img) => Number(img.id) === Number(variant.image_id));
        if (image?.src) return image.src;
      }
      return this.config.fallbackImage || '';
    }

    setVariant(variantId, options = {}) {
      const variant = this.getVariantById(variantId);
      if (!variant) return;

      this.state.variantId = Number(variant.id);
      const imageSrc = this.getVariantImage(variant);
      const colorLabel = this.getVariantColor(variant);

      if (this.mainImage && imageSrc) {
        this.mainImage.src = imageSrc;
        this.mainImage.alt = `${this.config.headingLine2 || 'Product'} - ${colorLabel}`;
      }

      if (this.selectedColorLabel) {
        this.selectedColorLabel.textContent = `(${colorLabel})`;
      }

      this.swatchButtons.forEach((button) => {
        const isActive = Number(button.dataset.variantId) === this.state.variantId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.updateAddToCartLabel();

      if (!options.silent) {
        this.root.dispatchEvent(
          new CustomEvent('lss:variant-change', {
            bubbles: true,
            detail: { variantId: this.state.variantId, variant },
          })
        );
      }
    }

    setVoucherLoading(isLoading) {
      this.state.voucherLoading = isLoading;
      if (this.voucherApplyBtn) {
        this.voucherApplyBtn.disabled = isLoading;
        this.voucherApplyBtn.classList.toggle('is-loading', isLoading);
        this.voucherApplyBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    wait(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    setVoucherButtonMode(mode) {
      if (!this.voucherApplyBtn || !this.voucherActionText) return;

      const isRemove = mode === 'remove';
      const applyLabel = this.config.voucherApplyLabel || 'Apply';
      const removeLabel = this.config.voucherRemoveLabel || 'Remove';

      this.voucherApplyBtn.classList.toggle('is-remove', isRemove);
      this.voucherActionText.textContent = isRemove ? removeLabel : applyLabel;
      this.voucherApplyBtn.setAttribute(
        'aria-label',
        isRemove ? removeLabel : applyLabel
      );
    }

    setVoucherMessage(message, type) {
      if (!this.voucherError) return;

      if (type === 'clear' || !message) {
        this.voucherError.textContent = '';
        this.voucherError.classList.remove('is-visible');
        return;
      }

      this.voucherError.textContent = message;
      this.voucherError.classList.add('is-visible');
    }

    showVoucherAppliedUI(code) {
      const displayCode = String(code).trim().toUpperCase();

      if (this.voucherRow) this.voucherRow.classList.add('is-applied');
      if (this.voucherInput) {
        this.voucherInput.value = displayCode;
        this.voucherInput.disabled = true;
      }
      this.setVoucherButtonMode('remove');
    }

    clearVoucherAppliedUI() {
      if (this.voucherRow) this.voucherRow.classList.remove('is-applied');
      if (this.voucherInput) {
        this.voucherInput.disabled = false;
        this.voucherInput.value = '';
      }
      this.setVoucherButtonMode('apply');
    }

    getVariantPrice(variant) {
      if (!variant) return Number(this.config.defaultVariantPrice || 0);
      const price = variant.price ?? this.config.defaultVariantPrice;
      return Number(price || 0);
    }

    getAddToCartPrefix() {
      const raw = String(this.config.addToCartPrefix || 'Add to cart -').trimEnd();
      if (raw.endsWith('-')) return `${raw} `;
      return `${raw} - `;
    }

    updateAddToCartLabel() {
      if (!this.addToCartText) return;

      if (this.state.voucherApplied) {
        const prefix = this.getAddToCartPrefix().toUpperCase();
        this.addToCartText.textContent = `${prefix}${this.formatMoney(0)}`;
        if (this.addToCartIcon) this.addToCartIcon.hidden = false;
        return;
      }

      const variant = this.getVariantById(this.state.variantId);
      this.addToCartText.textContent = `${this.getAddToCartPrefix()}${this.formatMoney(this.getVariantPrice(variant))}`;
      if (this.addToCartIcon) this.addToCartIcon.hidden = false;
    }

    parseAllowedCodes() {
      return String(this.config.validVoucherCodes || '')
        .split(/[\n,]/)
        .map((code) => code.trim().toLowerCase())
        .filter(Boolean);
    }

    validateVoucherCode(code) {
      const normalized = code.trim();
      if (!normalized) {
        return { valid: false, message: this.config.messages?.empty || 'Please enter a voucher code.' };
      }

      const allowedCodes = this.parseAllowedCodes();
      if (!allowedCodes.length) {
        return { valid: false, message: this.config.messages?.invalid || 'Invalid voucher code.' };
      }

      if (!allowedCodes.includes(normalized.toLowerCase())) {
        return { valid: false, message: this.config.messages?.invalid || 'Invalid voucher code.' };
      }

      return { valid: true, code: normalized };
    }

    async applyVoucher(options = {}) {
      if (!this.voucherInput || this.state.voucherLoading || this.state.voucherApplied) return;

      const code = this.voucherInput.value.trim();
      const skipDelay = Boolean(options.skipDelay);
      this.setVoucherMessage('', 'clear');
      this.setVoucherLoading(true);

      const startedAt = Date.now();

      try {
        const result = this.validateVoucherCode(code);

        if (!skipDelay) {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, 1000 - elapsed);
          if (remaining) await this.wait(remaining);
        }

        if (!result.valid) {
          this.state.voucherApplied = false;
          this.state.voucherCode = '';
          this.root.dataset.voucherApplied = 'false';
          this.clearVoucherAppliedUI();
          this.setVoucherMessage(result.message, 'error');
          sessionStorage.removeItem(this.storageKey('voucher'));
          this.updateAddToCartLabel();
          return;
        }

        this.state.voucherApplied = true;
        this.state.voucherCode = result.code;
        this.root.dataset.voucherApplied = 'true';
        sessionStorage.setItem(this.storageKey('voucher'), result.code);
        this.showVoucherAppliedUI(result.code);
        this.updateAddToCartLabel();
        void this.applyVoucherDiscountToSession().catch(() => {});
      } finally {
        this.setVoucherLoading(false);
      }
    }

    async removeVoucher() {
      if (!this.state.voucherApplied || this.state.voucherLoading) return;

      const removeMinDelayMs = 1500;
      this.setVoucherMessage('', 'clear');
      this.setVoucherLoading(true);
      const startedAt = Date.now();

      try {
        await this.clearVoucherDiscountFromSession();

        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, removeMinDelayMs - elapsed);
        if (remaining) await this.wait(remaining);

        this.state.voucherApplied = false;
        this.state.voucherCode = '';
        this.root.dataset.voucherApplied = 'false';
        sessionStorage.removeItem(this.storageKey('voucher'));
        this.clearVoucherAppliedUI();
        this.updateAddToCartLabel();
      } catch (err) {
        console.error('[LSS] Remove voucher failed', err);

        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, removeMinDelayMs - elapsed);
        if (remaining) await this.wait(remaining);

        this.setVoucherMessage(this.config.messages?.error || 'Unable to remove coupon. Please try again.', 'error');
      } finally {
        this.setVoucherLoading(false);
      }
    }

    restoreVoucherState() {
      const saved = sessionStorage.getItem(this.storageKey('voucher'));
      if (!saved || !this.voucherInput) return;

      const result = this.validateVoucherCode(saved);
      if (!result.valid) {
        sessionStorage.removeItem(this.storageKey('voucher'));
        return;
      }

      this.voucherInput.value = saved;
      this.state.voucherApplied = true;
      this.state.voucherCode = result.code;
      this.root.dataset.voucherApplied = 'true';
      this.showVoucherAppliedUI(result.code);
      this.updateAddToCartLabel();
    }

    storageKey(suffix) {
      return `lss-${this.sectionId}-${suffix}`;
    }

    setAddToCartLoading(isLoading) {
      if (!this.addToCartBtn) return;
      this.addToCartBtn.disabled = isLoading;
      this.addToCartBtn.classList.toggle('is-loading', isLoading);
      this.addToCartBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    async clearVoucherDiscountFromSession() {
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: '' }),
        });

        if (!response.ok) {
          throw new Error('Unable to clear discount from cart.');
        }
      } catch (err) {
        console.warn('[LSS] Unable to clear voucher discount from session', err);
        throw err;
      }
    }

    async applyVoucherDiscountToSession() {
      if (!this.state.voucherApplied || !this.state.voucherCode) return;

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: this.state.voucherCode }),
      });

      if (!response.ok) {
        throw new Error('Unable to apply discount to cart.');
      }
    }

    async handleAddToCart() {
      this.setAddToCartLoading(true);
      const voucherAddMinDelayMs = 3250;
      const startedAt = Date.now();

      try {
        if (this.state.voucherApplied) {
          await this.applyVoucherDiscountToSession();

          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, voucherAddMinDelayMs - elapsed);
          if (remaining) await this.wait(remaining);
        }

        const items = [{ id: this.state.variantId, quantity: 1 }];

        if (this.rebuyState?.selectedVariantIds?.size) {
          for (const variantId of this.rebuyState.selectedVariantIds) {
            items.push({
              id: variantId,
              quantity: 1,
              properties: { _widget_id: String(this.rebuyWidgetId || '') },
            });
          }
        }

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add to cart.');
        }

        if (this.rebuyState?.selectedVariantIds) {
          this.rebuyState.selectedVariantIds.clear();
          this.renderRebuyUi();
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {
        console.error('[LSS] Add to cart failed', err);
        this.setVoucherMessage(err.message || 'Unable to add to cart. Please try again.', 'error');
      } finally {
        this.setAddToCartLoading(false);
      }
    }

    initRebuy() {
      if (!this.rebuyUi || !this.rebuyHost) return;

      this.rebuyState = {
        products: [],
        loading: true,
        variantSelections: {},
        selectedVariantIds: new Set(),
      };

      const widgetId = this.config.rebuyWidgetId || this.rebuyHost.dataset.rebuyId;
      this.rebuyWidgetId = widgetId;
      this.rebuyLoadPromise = null;

      if (typeof window.Rebuy?.init === 'function') {
        try {
          window.Rebuy.init();
        } catch (err) {
          console.warn('[LSS] Rebuy.init failed', err);
        }
      }

      this.onRebuyReady = () => {
        if (!this.rebuyState?.products?.length && !this.rebuyLoadPromise) {
          this.loadRebuyProducts({ background: true });
        }
      };
      document.addEventListener('rebuy.ready', this.onRebuyReady);

      this.loadRebuyProducts();
    }

    getRebuyCacheKey() {
      return `lss-rebuy-v1-${this.rebuyWidgetId}-${this.config.mainProductId || 'default'}`;
    }

    readRebuyCache() {
      try {
        const raw = sessionStorage.getItem(this.getRebuyCacheKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.items?.length || Date.now() - parsed.ts > 5 * 60 * 1000) return null;
        return parsed.items;
      } catch (err) {
        return null;
      }
    }

    writeRebuyCache(items) {
      if (!items?.length) return;
      try {
        sessionStorage.setItem(
          this.getRebuyCacheKey(),
          JSON.stringify({
            ts: Date.now(),
            items,
          })
        );
      } catch (err) {
        console.warn('[LSS] Rebuy cache write failed', err);
      }
    }

    getRebuyApiKey() {
      return window.Rebuy?.shop?.api_key || window.Rebuy?.shop?.apiKey || '';
    }

    getRebuyRulesetId(widget) {
      if (widget && String(widget.id) !== String(this.rebuyWidgetId)) return '';

      return (
        this.config.rebuyRulesetId ||
        widget?.data?.metadata?.ruleset_id ||
        widget?.data?.settings?.ruleset_id ||
        widget?.data?.settings?.data_source_id ||
        ''
      );
    }

    isConfiguredRebuyWidget(widget) {
      return Boolean(widget && String(widget.id) === String(this.rebuyWidgetId));
    }

    applyRebuyContextToWidget(widget) {
      if (!this.isConfiguredRebuyWidget(widget) || !widget?.data) return;
      const productId = this.config.mainProductId || this.product?.id;
      const variantId = this.state.variantId;
      if (productId) {
        widget.data.shopify_product_ids = [Number(productId)];
      }
      if (variantId) {
        widget.data.shopify_variant_ids = [Number(variantId)];
      }
    }

    applyRebuyProducts(rawProducts) {
      this.rebuyState.products = (rawProducts || [])
        .map((product, index) => this.normalizeRebuyProduct(product, index))
        .filter(Boolean);

      if (this.rebuyState.products.length) {
        this.writeRebuyCache(this.rebuyState.products);
      }
    }

    async fetchProductsFromRebuyApi(widget) {
      if (!this.isConfiguredRebuyWidget(widget)) return [];

      const apiKey = this.getRebuyApiKey();
      const rulesetId = this.getRebuyRulesetId(widget);
      if (!apiKey || !rulesetId) return [];

      const params = new URLSearchParams({
        key: apiKey,
        limit: '8',
        filter_oos: 'yes',
        widget_id: String(this.rebuyWidgetId),
      });

      const productId = this.config.mainProductId || this.product?.id;
      const variantId = this.state.variantId;
      if (productId) params.set('shopify_product_ids', String(productId));
      if (variantId) params.set('shopify_variant_ids', String(variantId));

      const url = `https://rebuyengine.com/api/v1/custom/id/${rulesetId}?${params}`;

      try {
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) return [];
        const json = await response.json();
        return json.data || [];
      } catch (err) {
        console.warn('[LSS] Rebuy API fetch failed', err);
        return [];
      }
    }

    findRebuyWidget() {
      const widgets = window.Rebuy?.widgets || [];
      return widgets.find((widget) => String(widget.id) === String(this.rebuyWidgetId)) || null;
    }

    waitForRebuyWidget(timeout = 5000) {
      return new Promise((resolve) => {
        const immediate = this.findRebuyWidget();
        if (immediate) {
          resolve(immediate);
          return;
        }

        let settled = false;
        const finish = (widget) => {
          if (settled) return;
          settled = true;
          document.removeEventListener('rebuy.ready', onReady);
          clearInterval(poll);
          clearTimeout(timer);
          resolve(widget || null);
        };

        const onReady = (event) => {
          const widget = event.detail?.widget;
          if (widget && String(widget.id) === String(this.rebuyWidgetId)) finish(widget);
        };

        document.addEventListener('rebuy.ready', onReady);
        const poll = setInterval(() => {
          const widget = this.findRebuyWidget();
          if (widget) finish(widget);
        }, 100);
        const timer = setTimeout(() => finish(this.findRebuyWidget()), timeout);
      });
    }

    fetchProductsFromWidget(widget, timeoutMs = 2500) {
      return new Promise((resolve) => {
        if (!this.isConfiguredRebuyWidget(widget)) {
          resolve([]);
          return;
        }

        this.applyRebuyContextToWidget(widget);

        const existing = widget.data?.products;
        if (Array.isArray(existing) && existing.length) {
          resolve(existing);
          return;
        }

        if (typeof widget.getWidgetProducts !== 'function') {
          resolve(Array.isArray(existing) ? existing : []);
          return;
        }

        let settled = false;
        const finish = (products) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(Array.isArray(products) ? products : []);
        };

        const timer = setTimeout(() => finish(widget.data?.products || existing || []), timeoutMs);

        try {
          widget.getWidgetProducts(() => {
            finish(widget.data?.products || existing || []);
          });
        } catch (err) {
          console.warn('[LSS] getWidgetProducts failed', err);
          finish(existing || []);
        }
      });
    }

    parseRebuyMoney(value) {
      if (value == null || value === '') return 0;
      if (typeof value === 'number') return value > 1000 ? value : Math.round(value * 100);
      const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, this.config.moneyFormat || '${{amount}}');
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    isDefaultVariantTitle(title) {
      const normalized = String(title || '').trim().toLowerCase();
      return normalized === 'default title' || normalized === 'default';
    }

    getDisplayRebuyVariants(variants) {
      return (variants || []).filter((variant) => !this.isDefaultVariantTitle(variant.title));
    }

    mapRebuyVariant(variant, product, fallbackImage) {
      if (!variant?.id) return null;
      const image =
        variant.image?.src ||
        variant.featured_image?.src ||
        variant.featured_image ||
        fallbackImage ||
        '';
      const price = this.parseRebuyMoney(variant.price);
      const compareAt = this.parseRebuyMoney(variant.compare_at_price);
      return {
        id: Number(variant.id),
        title: String(variant.title || variant.public_title || variant.option1 || ''),
        price,
        compareAt: compareAt > price ? compareAt : 0,
        image: typeof image === 'string' ? image : image?.src || '',
      };
    }

    applyRebuyVariant(item, variant) {
      if (!item || !variant) return item;
      item.variantId = variant.id;
      item.price = variant.price;
      item.compareAt = variant.compareAt;
      item.image = variant.image || item.image;
      item.variantTitle = variant.title;
      return item;
    }

    getRebuyItemWithSelection(item) {
      if (!item) return null;
      const savedVariantId = this.rebuyState.variantSelections[item.uid];
      const variant =
        item.variants.find((entry) => Number(entry.id) === Number(savedVariantId)) ||
        item.variants.find((entry) => Number(entry.id) === Number(item.variantId)) ||
        item.variants[0];
      return this.applyRebuyVariant({ ...item }, variant);
    }

    normalizeRebuyProduct(product, index) {
      if (!product) return null;

      const productId = Number(product.id || product.product_id);
      const mainProductId = Number(this.config.mainProductId || this.product?.id);
      const mainProductTitle = String(this.config.mainProductTitle || this.product?.title || '').toLowerCase();
      const title = product.title || product.product_title || product.name;

      if (productId && mainProductId && productId === mainProductId) return null;
      if (mainProductTitle && title && String(title).toLowerCase() === mainProductTitle) return null;
      const fallbackImage =
        product.image?.src || product.image || product.featured_image || product.images?.[0]?.src || product.image_url || '';

      const rawVariants = Array.isArray(product.variants) ? product.variants : [];
      const variants = rawVariants
        .map((variant) => this.mapRebuyVariant(variant, product, fallbackImage))
        .filter(Boolean);

      const defaultVariant = product.selected_variant || rawVariants[0];
      const initialVariant =
        this.mapRebuyVariant(defaultVariant, product, fallbackImage) ||
        variants[0] ||
        this.mapRebuyVariant(
          {
            id: product.variant_id || product.variantId,
            title: product.variant_title,
            price: product.price ?? product.price_cents,
            compare_at_price: product.compare_at_price ?? product.compareAt,
          },
          product,
          fallbackImage
        );

      if (!title || !initialVariant?.id) return null;

      const uid = `${productId || initialVariant.id}-${index}`;
      const item = {
        uid,
        productId,
        variantId: initialVariant.id,
        title: String(title),
        image: initialVariant.image || (typeof fallbackImage === 'string' ? fallbackImage : fallbackImage?.src || ''),
        price: initialVariant.price,
        compareAt: initialVariant.compareAt,
        variantTitle: initialVariant.title,
        variants,
      };

      return this.getRebuyItemWithSelection(item);
    }

    async loadRebuyProducts(options = {}) {
      if (!this.rebuyUi) return;
      if (this.rebuyLoadPromise) return this.rebuyLoadPromise;

      const background = options.background === true;
      const cached = this.readRebuyCache();

      if (cached?.length && !background) {
        this.rebuyState.products = cached;
        this.rebuyState.loading = false;
        this.renderRebuyUi();
        this.rebuyLoadPromise = this.fetchRebuyProductsFresh({ background: true }).finally(() => {
          this.rebuyLoadPromise = null;
        });
        return this.rebuyLoadPromise;
      }

      if (!background) {
        this.rebuyState.loading = true;
        this.renderRebuyUi();
      }

      this.rebuyLoadPromise = this.fetchRebuyProductsFresh({ background }).finally(() => {
        this.rebuyLoadPromise = null;
      });
      return this.rebuyLoadPromise;
    }

    async fetchRebuyProductsFresh(options = {}) {
      const background = options.background === true;
      const widget = await this.waitForRebuyWidget(5000);

      if (!this.isConfiguredRebuyWidget(widget)) {
        this.rebuyState.loading = false;
        this.renderRebuyUi();
        return;
      }

      this.applyRebuyContextToWidget(widget);
      const existing = widget.data?.products;
      if (Array.isArray(existing) && existing.length) {
        this.applyRebuyProducts(existing);
        this.rebuyState.loading = false;
        this.renderRebuyUi();
        return;
      }

      const widgetProducts = await this.fetchProductsFromWidget(widget, 2500);
      let products = widgetProducts;

      if (!products.length) {
        products = await this.fetchProductsFromRebuyApi(widget);
      }

      if (products.length) {
        this.applyRebuyProducts(products);
      }

      this.rebuyState.loading = false;
      if (!background || this.rebuyState.products.length) {
        this.renderRebuyUi();
      }
    }

    isAddonSelected(variantId) {
      return this.rebuyState?.selectedVariantIds?.has(Number(variantId));
    }

    buildRebuyCardMarkup(item) {
      const isOn = this.isAddonSelected(item.variantId);
      const displayVariants = this.getDisplayRebuyVariants(item.variants);
      const hasVariants = displayVariants.length > 1;
      const showVariantTitle =
        !hasVariants && item.variantTitle && !this.isDefaultVariantTitle(item.variantTitle);

      const variantMarkup = hasVariants
        ? `<select class="lss-rebuy-variant-select" data-lss-rebuy-variant data-lss-rebuy-uid="${this.escapeHtml(item.uid)}" aria-label="Select add-on variant for ${this.escapeHtml(item.title)}">
            ${displayVariants
              .map(
                (variant) =>
                  `<option value="${variant.id}"${Number(variant.id) === Number(item.variantId) ? ' selected' : ''}>${this.escapeHtml(variant.title)}</option>`
              )
              .join('')}
          </select>`
        : showVariantTitle
          ? `<p class="lss-rebuy-card__variant">${this.escapeHtml(item.variantTitle)}</p>`
          : '';

      return `
        <article class="lss-rebuy-card" data-lss-rebuy-uid="${this.escapeHtml(item.uid)}">
          <img class="lss-rebuy-card__image" src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" width="112" height="112" loading="lazy">
          <div class="lss-rebuy-card__body">
            <p class="lss-rebuy-card__title">${this.escapeHtml(item.title)}</p>
            <div class="lss-rebuy-card__prices">
              <p class="lss-rebuy-card__sale">${this.escapeHtml(this.formatMoney(item.price))}</p>
              ${item.compareAt ? `<p class="lss-rebuy-card__compare">${this.escapeHtml(this.formatMoney(item.compareAt))}</p>` : ''}
            </div>
            ${variantMarkup}
          </div>
          <button
            type="button"
            class="lss-rebuy-toggle${isOn ? ' is-on' : ''}"
            data-lss-rebuy-toggle
            data-lss-rebuy-uid="${this.escapeHtml(item.uid)}"
            data-variant-id="${item.variantId}"
            aria-label="${isOn ? 'Remove add-on from order' : 'Include add-on with order'}"
            aria-pressed="${isOn ? 'true' : 'false'}"
          >
            <span class="lss-rebuy-toggle__knob"></span>
          </button>
        </article>
      `;
    }

    bindRebuyUiEvents() {
      this.rebuyUi.querySelectorAll('[data-lss-rebuy-variant]').forEach((select) => {
        select.addEventListener('change', (event) => {
          const uid = event.currentTarget.dataset.lssRebuyUid;
          const variantId = Number(event.currentTarget.value);
          this.selectRebuyVariant(uid, variantId);
        });
      });

      this.rebuyUi.querySelectorAll('[data-lss-rebuy-toggle]').forEach((button) => {
        button.addEventListener('click', (event) => {
          const target = event.currentTarget;
          const uid = target.dataset.lssRebuyUid;
          const variantId = Number(target.dataset.variantId);
          this.toggleRebuyAddon(uid, variantId, !target.classList.contains('is-on'));
        });
      });
    }

    renderRebuyUi() {
      if (!this.rebuyUi || !this.rebuyState) return;

      if (this.rebuyState.loading) {
        this.rebuyUi.innerHTML = '<p class="lss-rebuy-loading">Loading add-ons...</p>';
        return;
      }

      if (!this.rebuyState.products.length) {
        this.rebuyUi.innerHTML = '<p class="lss-rebuy-empty">No add-ons available right now.</p>';
        return;
      }

      const cards = this.rebuyState.products
        .map((product) => this.getRebuyItemWithSelection(product))
        .filter(Boolean)
        .map((item) => this.buildRebuyCardMarkup(item))
        .join('');

      this.rebuyUi.innerHTML = `<div class="lss-rebuy-stack">${cards}</div>`;
      this.bindRebuyUiEvents();
    }

    selectRebuyVariant(uid, variantId) {
      const product = this.rebuyState.products.find((entry) => entry.uid === uid);
      if (!product) return;

      const variant = product.variants.find((entry) => Number(entry.id) === Number(variantId));
      if (!variant) return;

      const previousVariantId = Number(product.variantId);
      const wasSelected = this.isAddonSelected(previousVariantId);

      this.rebuyState.variantSelections[product.uid] = variant.id;
      this.applyRebuyVariant(product, variant);

      if (wasSelected) {
        this.rebuyState.selectedVariantIds.delete(previousVariantId);
        this.rebuyState.selectedVariantIds.add(variant.id);
      }

      this.renderRebuyUi();
    }

    toggleRebuyAddon(uid, variantId, enable) {
      if (!variantId) return;

      const product = this.rebuyState.products.find((entry) => entry.uid === uid);
      if (!product) return;

      const selectedVariantId = Number(product.variantId);
      if (enable) {
        this.rebuyState.selectedVariantIds.add(selectedVariantId);
      } else {
        this.rebuyState.selectedVariantIds.delete(selectedVariantId);
      }

      this.renderRebuyUi();
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  function initAll() {
    document.querySelectorAll('[data-lss-root]').forEach((root) => {
      if (root.__lssInitialized) return;
      root.__lssInitialized = true;
      new LuxurySteeringSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-lss-root]');
    if (root) {
      root.__lssInitialized = false;
      initAll();
    }
  });
})();
